import { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { startOfDay, parseISO, format, eachDayOfInterval, subDays } from 'date-fns';
import type { LessonSession, TimelineMode } from './types';
import styles from './ProgressCharts.module.css';

interface ProgressChartProps {
  sessions: LessonSession[];
  includeAppName?: boolean;
  title?: string;
}

interface DailyProgressData {
  date: string;
  displayDate: string;
  progress: number;
}

export function ProgressChart({
  sessions,
  includeAppName = false,
  title = 'Progress',
}: ProgressChartProps) {
  const [timelineMode, setTimelineMode] = useState<TimelineMode>('30d');

  const { chartData, currentProgress } = useMemo(() => {
    const now = new Date();
    const daysBack = timelineMode === '7d' ? 6 : timelineMode === '30d' ? 29 : 89;
    const startDate = startOfDay(subDays(now, daysBack));
    const endDate = startOfDay(now);

    if (sessions.length === 0) {
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      return {
        chartData: allDays.map(day => ({
          date: format(day, 'yyyy-MM-dd'),
          displayDate: format(day, 'MMM dd'),
          progress: 0,
        })),
        currentProgress: 0,
      };
    }

    // Sort sessions chronologically (oldest first)
    const sortedSessions = [...sessions].sort(
      (a, b) => new Date(a.completed_at).getTime() - new Date(b.completed_at).getTime()
    );

    // Track the highest unit reached per key
    // Key is either "schedule" or "appName:schedule" based on includeAppName
    const progressMap = new Map<string, number>();

    // Build a map of date -> cumulative progress
    const dailyProgressMap = new Map<string, number>();

    sortedSessions.forEach(session => {
      if (session.practice_mode !== 'schedule' || !session.schedule || session.unit_number === null) {
        return;
      }

      const key = includeAppName
        ? `${session.app_name}:${session.schedule}`
        : session.schedule;
      const currentMax = progressMap.get(key) ?? 0;

      // Only update if this session represents higher progress and was passed
      if (session.passed && session.unit_number > currentMax) {
        progressMap.set(key, session.unit_number);
      }

      // Calculate total progress as sum of all highest units
      const totalProgress = Array.from(progressMap.values()).reduce((sum, val) => sum + val, 0);

      const dateKey = format(startOfDay(parseISO(session.completed_at)), 'yyyy-MM-dd');
      dailyProgressMap.set(dateKey, totalProgress);
    });

    // Fill in all days, carrying forward the last known progress
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    let lastProgress = 0;

    // Find progress before the start date
    for (const [dateStr, progress] of dailyProgressMap) {
      if (dateStr < format(startDate, 'yyyy-MM-dd')) {
        lastProgress = progress;
      }
    }

    const data: DailyProgressData[] = allDays.map(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      if (dailyProgressMap.has(dateKey)) {
        lastProgress = dailyProgressMap.get(dateKey)!;
      }
      return {
        date: dateKey,
        displayDate: format(day, 'MMM dd'),
        progress: lastProgress,
      };
    });

    // Get current progress (last value)
    const current = data.length > 0 ? data[data.length - 1].progress : 0;

    return { chartData: data, currentProgress: current };
  }, [sessions, timelineMode, includeAppName]);

  // Use unique gradient ID to avoid conflicts when multiple charts are rendered
  const gradientId = includeAppName ? 'overallProgressGradient' : 'appProgressGradient';

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div>
          <h3 className={styles.chartTitle}>{title}</h3>
          <p className={styles.chartSubtitle}>{currentProgress} units</p>
        </div>
        <select
          className={styles.timelineSelect}
          value={timelineMode}
          onChange={(e) => setTimelineMode(e.target.value as TimelineMode)}
        >
          <option value="7d">7 days</option>
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
        </select>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className={styles.gridLine} />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval={timelineMode === '7d' ? 0 : timelineMode === '30d' ? 6 : 14}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              domain={[0, Math.max(10, currentProgress + 5)]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value) => [`${value} units`, 'Progress']}
              labelFormatter={(label) => label}
            />
            <Area
              type="stepAfter"
              dataKey="progress"
              stroke="#22c55e"
              fill={`url(#${gradientId})`}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
